import { getRequestInstance } from "api/services/utils";
import { RequestTypes } from "api/services/enums";
import {
  AdvisoryBuyerArchiveDocument,
  AdvisoryBuyerGetByIdDocument,
  AdvisoryBuyerListDocument,
  AdvisoryBuyersPaginatedDocument,
  AdvisoryBuyerCreateDocument,
  AdvisoryBuyerUnarchiveDocument,
  AdvisoryBuyerUpdateDocument,
  AdvisoryBuyerListWalletTypeDocument,
  AdvisoryBuyerListCurrencyTypeDocument,
  AdvisoryBuyerDeleteDocument,
} from "api/generated";

import { Optional, ScopeEnum, ResourceEnum } from "config";
import { executeEviction } from "api/utils/cache-eviction";
import { client } from "config/apollo";

import {
  IAdvisoryBuyerModel,
  IAdvisoryBuyerCreateVars,
  IAdvisoryBuyerPaginatedResponse,
  IAdvisoryBuyerPaginatedVars,
  IAdvisoryBuyerUpdateVars,
  IAdvisoryListModel,
} from "./types";

export async function getAdvisoryBuyerListWalletType(): Promise<string[]> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: AdvisoryBuyerListWalletTypeDocument,
      fetchPolicy: "cache-first",
    });

    return res?.data?.advisoryBuyerListWalletType ?? [];
  } catch (error) {
    throw error;
  }
}

export async function getAdvisoryBuyerListCurrencyType(): Promise<string[]> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: AdvisoryBuyerListCurrencyTypeDocument,
      fetchPolicy: "cache-first",
    });

    return res?.data?.advisoryBuyerListCurrencyType ?? [];
  } catch (error) {
    throw error;
  }
}

export async function getAdvisoryBuyer(
  advisoryBuyerId: number
): Promise<Optional<IAdvisoryBuyerModel>> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: AdvisoryBuyerGetByIdDocument,
      variables: {
        id: advisoryBuyerId,
      },
      fetchPolicy: "cache-first",
    });

    return res?.data?.advisoryBuyerGetById;
  } catch (error) {
    throw error;
  }
}

export async function getAdvisoryBuyerPaginated(
  variables: IAdvisoryBuyerPaginatedVars
): Promise<Optional<IAdvisoryBuyerPaginatedResponse>> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: AdvisoryBuyersPaginatedDocument,
      variables,
      fetchPolicy: "cache-first",
    });

    return res?.data?.advisoryBuyerListPaginated;
  } catch (error) {
    throw error;
  }
}

export async function getAdvisoryBuyerList(
  resource: ResourceEnum,
  scope: ScopeEnum
): Promise<IAdvisoryListModel[]> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: AdvisoryBuyerListDocument,
      variables: {
        scope,
        resource,
      },
      fetchPolicy: "cache-first",
    });

    return res?.data?.advisoryBuyerList || [];
  } catch (error) {
    throw error;
  }
}

export async function createAdvisoryBuyer(
  variables: IAdvisoryBuyerCreateVars
): Promise<Optional<{ advisoryBuyerId: number }>> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: AdvisoryBuyerCreateDocument,
      variables,
      fetchPolicy: "network-only",
    });

    // Evict advisory buyer lists
    executeEviction(client, {
      typename: "AdvisoryBuyer",
      fieldNames: ["advisoryBuyerListPaginated", "advisoryBuyerList"],
    });

    return res?.data?.advisoryBuyerCreate;
  } catch (error) {
    throw error;
  }
}

export async function updateAdvisoryBuyer(
  variables: IAdvisoryBuyerUpdateVars
): Promise<Optional<{ advisoryBuyerId: number }>> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: AdvisoryBuyerUpdateDocument,
      variables,
      fetchPolicy: "network-only",
    });

    // Evict updated entity + all lists + getById queries
    executeEviction(client, {
      typename: "AdvisoryBuyer",
      id: variables.advisoryBuyerId.toString(),
      fieldNames: [
        "advisoryBuyerListPaginated",
        "advisoryBuyerList",
        "advisoryBuyerGetById",
      ],
    });

    return res?.data?.advisoryBuyerUpdate;
  } catch (error) {
    throw error;
  }
}

export async function archiveAdvisoryBuyer(
  advisoryBuyerId: number
): Promise<boolean> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: AdvisoryBuyerArchiveDocument,
      variables: {
        advisoryBuyerId,
      },
      fetchPolicy: "no-cache",
    });

    // Evict archived entity + all lists
    executeEviction(client, {
      typename: "AdvisoryBuyer",
      id: advisoryBuyerId.toString(),
      fieldNames: ["advisoryBuyerListPaginated", "advisoryBuyerList"],
    });

    return Boolean(res?.data?.advisoryBuyerArchive);
  } catch (error) {
    throw error;
  }
}

export async function deleteAdvisoryBuyer(
  advisoryBuyerId: number
): Promise<boolean> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: AdvisoryBuyerDeleteDocument,
      variables: {
        advisoryBuyerId,
      },
      fetchPolicy: "no-cache",
    });

    // Evict deleted entity + all lists
    executeEviction(client, {
      typename: "AdvisoryBuyer",
      id: advisoryBuyerId.toString(),
      fieldNames: ["advisoryBuyerListPaginated", "advisoryBuyerList"],
    });

    return Boolean(res?.data?.advisoryBuyerDelete);
  } catch (error) {
    throw error;
  }
}

export async function unarchiveAdvisoryBuyer(
  advisoryBuyerId: number
): Promise<boolean> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: AdvisoryBuyerUnarchiveDocument,
      variables: {
        advisoryBuyerId,
      },
      fetchPolicy: "no-cache",
    });

    // Evict unarchived entity + all lists
    executeEviction(client, {
      typename: "AdvisoryBuyer",
      id: advisoryBuyerId.toString(),
      fieldNames: ["advisoryBuyerListPaginated", "advisoryBuyerList"],
    });

    return Boolean(res?.data?.advisoryBuyerUnarchive);
  } catch (error) {
    throw error;
  }
}
